import { DashboardInfo } from 'schemas/DashboardInfoSchema'
import { getMemorizedStateFromSessionStorage } from 'utils/sessionStorageUtils'

import { DashboardInfoBaseState, DashboardSortBy, DashboardSortOrder } from './types'
import { SORTING_LEVEL, SORT_BY, SORT_ORDER } from './constants'

// * sorting functions

const compareNumberByOrder = (order: string) => (a: number, b: number) =>
  order === SORT_ORDER.ASC ? a - b : b - a

export const compareStringByOrder = (order: DashboardSortOrder) => (a: string, b: string) => {
  if (a !== b) {
    if (!a) return 1
    if (!b) return -1
  }

  if (a < b) {
    return order === SORT_ORDER.ASC ? -1 : 1
  }
  if (a > b) {
    return order === SORT_ORDER.ASC ? 1 : -1
  }
  return 0
}

export const compareName = (order: DashboardSortOrder) => (a: DashboardInfo, b: DashboardInfo) => {
  const stringA = a.name!
  const stringB = b.name!
  const compareString = compareStringByOrder(order)
  return compareString(stringA, stringB)
}

export const compareLabel = (order: DashboardSortOrder) => (a: DashboardInfo, b: DashboardInfo) => {
  const stringA = a.labels?.[0] ?? ''
  const stringB = b.labels?.[0] ?? ''
  const compareString = compareStringByOrder(order)
  return compareString(stringA, stringB)
}

export const compareDateCreated = (order: DashboardSortOrder) => (
  a: DashboardInfo,
  b: DashboardInfo
) => {
  const numberA = Number(a.createDatetime)
  const numberB = Number(b.createDatetime)
  const compareNumber = compareNumberByOrder(order)
  return compareNumber(numberA, numberB)
}

export const compareDateUpdated = (order: DashboardSortOrder) => (
  a: DashboardInfo,
  b: DashboardInfo
) => {
  const numberA = Number(a.updateDatetime)
  const numberB = Number(b.updateDatetime)
  const compareNumber = compareNumberByOrder(order)
  return compareNumber(numberA, numberB)
}

export const compareLiveReport = (order: DashboardSortOrder) => (
  a: DashboardInfo,
  b: DashboardInfo
) => {
  const numberA = Number(a.liveReportEnabled)
  const numberB = Number(b.liveReportEnabled)
  const compareNumber = compareNumberByOrder(order)
  return compareNumber(numberA, numberB)
}

export const compareCreatedBy = (order: DashboardSortOrder) => (
  a: DashboardInfo,
  b: DashboardInfo
) => {
  const stringA = a.ownerInfo.name!
  const stringB = b.ownerInfo.name!
  const compareString = compareStringByOrder(order)
  return compareString(stringA, stringB)
}

export const getSortingLevelBySortBy = (sortBy: DashboardSortBy) => {
  let newSortingLevel = SORTING_LEVEL.filter((sortField) => sortField !== sortBy)
  newSortingLevel = [sortBy, ...newSortingLevel]

  return newSortingLevel
}

export const multiCompareByOrder = ({
  order,
  sortingLevelArr
}: {
  order: DashboardSortOrder
  sortingLevelArr: string[]
}) => (a: DashboardInfo, b: DashboardInfo) => {
  let compareResult = 0
  sortingLevelArr.some((sortMethod) => {
    let compareFunc = compareDateCreated(order)
    switch (sortMethod) {
      case SORT_BY.DATE_CREATED:
        compareFunc = compareDateCreated(order)
        break
      case SORT_BY.DATE_UPDATED:
        compareFunc = compareDateUpdated(order)
        break
      case SORT_BY.NAME:
        compareFunc = compareName(order)
        break
      case SORT_BY.LABELS:
        compareFunc = compareLabel(order)
        break
      case SORT_BY.LIVE_REPORTS:
        compareFunc = compareLiveReport(order)
        break
      case SORT_BY.CREATED_BY:
        compareFunc = compareCreatedBy(order)
        break
      default:
        break
    }
    compareResult = compareFunc(a, b)
    // * not equal early return
    return compareResult !== 0
  })
  return compareResult
}

// * filter functions

export const isLiveReportEnabled = (dashboardInfo: DashboardInfo) =>
  dashboardInfo.liveReportEnabled === true

export const isScheduledReportEnabled = (dashboardInfo: DashboardInfo) =>
  dashboardInfo.scheduledReportEnabled === true

export const isMatchKeyword = (keyword: string) => (dashboardInfo: DashboardInfo) => {
  // * consider using string similarity instead of matching word
  const regex = new RegExp(keyword.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i')
  if (dashboardInfo.name!.search(regex) !== -1) {
    return true
  }

  if (dashboardInfo.labels) {
    return dashboardInfo.labels.some((label) => label!.search(regex) !== -1)
  }

  return false
}

export const multiFilter = (filter: DashboardInfoBaseState['filter']) => (
  dashboardInfo: DashboardInfo
) => {
  const filterFuncs = Object.entries(filter).reduce((acc, [filterKey, filterValue]) => {
    switch (filterKey) {
      case 'scheduledReportEnabled':
        return filterValue ? [...acc, isScheduledReportEnabled] : acc
      case 'liveReportEnabled':
        return filterValue ? [...acc, isLiveReportEnabled] : acc
      case 'keyword':
        return filterValue ? [...acc, isMatchKeyword(filterValue as string)] : acc
      default:
        return acc
    }
  }, [] as ((dashboardInfo: DashboardInfo) => boolean)[])

  return filterFuncs.length ? filterFuncs.every((filterFunc) => filterFunc(dashboardInfo)) : true
}

export const dashboardInfosToLabelEntity = (dashboardInfos: DashboardInfo[]) => {
  const labelsEntities = {} as DashboardInfoBaseState['labelsEntities']
  dashboardInfos.forEach((dashboard) => {
    if (dashboard.labels) {
      dashboard.labels.forEach((label) => {
        if (labelsEntities[label]) {
          labelsEntities[label] = [...labelsEntities[label], dashboard.id]
        } else {
          labelsEntities[label] = [dashboard.id]
        }
      })
    }
  })
  return labelsEntities
}

// * memorized state are from selectMemorizedStateForV4
export const getDefaultStatefromSessionStorage = () => {
  const memorizedState = getMemorizedStateFromSessionStorage()
  if (memorizedState) {
    return memorizedState.accessibleDashboards as Partial<DashboardInfoBaseState>
  }
  return null
}
